"use client";
import React, { useState } from "react";
import { Sidebar, SidebarBody, SidebarLink } from "./sidebar";
import { 
  Home, 
  Users, 
  Settings, 
  User, 
  Phone, 
  FileText,
  Shield,
  LogOut 
} from "lucide-react";
import { Link as RouterLink } from "react-router-dom";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

export function HudsonSidebarDemo() {
  const links = [
    {
      label: "Home",
      href: "/",
      icon: (
        <Home className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />
      ),
    },
    {
      label: "Membership",
      href: "#membership",
      icon: (
        <Users className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />
      ),
    },
    {
      label: "Services",
      href: "#services",
      icon: (
        <Settings className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />
      ),
    },
    {
      label: "My Account",
      href: "#account",
      icon: (
        <User className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />
      ),
    },
    {
      label: "Support",
      href: "#support",
      icon: (
        <Phone className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />
      ),
    },
    {
      label: "Documents",
      href: "#documents",
      icon: (
        <FileText className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />
      ),
    },
    {
      label: "Privacy",
      href: "#privacy",
      icon: (
        <Shield className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />
      ),
    },
  ];
  
  const [open, setOpen] = useState(false);
  
  return (
    <div
      className={cn(
        "rounded-md flex flex-col md:flex-row w-full flex-1 max-w-7xl mx-auto border border-neutral-200 dark:border-neutral-700 overflow-hidden",
        "h-[80vh]" // for your use case, use `h-screen` instead of `h-[80vh]`
      )}
      style={{ backgroundColor: 'rgb(13, 49, 53)' }}
    >
      <Sidebar open={open} setOpen={setOpen}>
        <SidebarBody className="justify-between gap-10">
          <div className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
            {open ? <HudsonLogo /> : <HudsonLogoIcon />}
            <div className="mt-8 flex flex-col gap-2">
              {links.map((link, idx) => (
                <SidebarLink key={idx} link={link} />
              ))}
            </div>
          </div>
          <div>
            <SidebarLink
              link={{
                label: "Member Portal",
                href: "#portal",
                icon: (
                  <div className="h-7 w-7 bg-gradient-to-r from-cyan-400 to-cyan-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="h-4 w-4 text-white" />
                  </div>
                ),
              }}
            />
            <div className="mt-4 pt-4 border-t border-neutral-300 dark:border-neutral-600">
              <SidebarLink
                link={{
                  label: "Sign Out",
                  href: "#signout",
                  icon: (
                    <LogOut className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />
                  ),
                }}
              />
            </div>
          </div>
        </SidebarBody>
      </Sidebar>
      <MemberDashboard />
    </div>
  );
}

export const HudsonLogo = () => {
  return (
    <RouterLink
      to="/"
      className="font-normal flex space-x-2 items-center text-sm py-1 relative z-20"
    >
      <div className="h-8 w-8 rounded-full overflow-hidden flex-shrink-0">
        <img
          src="https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/HMH_Secondary_Logo_Tape%20Turquoise.png"
          alt="Hudson Club"
          className="h-8 w-8 object-cover"
        />
      </div>
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="font-semibold text-white whitespace-pre"
      >
        The Hudson Club™
      </motion.span>
    </RouterLink>
  );
};

export const HudsonLogoIcon = () => {
  return (
    <RouterLink
      to="/"
      className="font-normal flex space-x-2 items-center text-sm py-1 relative z-20"
    >
      <div className="h-8 w-8 rounded-full overflow-hidden flex-shrink-0">
        <img
          src="https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/HMH_Secondary_Logo_Tape%20Turquoise.png"
          alt="Hudson Club"
          className="h-8 w-8 object-cover"
        />
      </div>
    </RouterLink>
  );
};

// Member Dashboard component with Hudson Club branding
const MemberDashboard = () => {
  return (
    <div className="flex flex-1">
      <div className="p-2 md:p-10 rounded-tl-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 flex flex-col gap-2 flex-1 w-full h-full">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Welcome Back, Member
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Your home is in excellent hands. Here's your personalized dashboard.
          </p>
        </div>

        {/* Quick Stats */}
        <div className="flex gap-2 mb-6">
          {[
            { label: "Active Services", value: "12", color: "bg-green-100 dark:bg-green-800" },
            { label: "This Month", value: "8 hrs saved", color: "bg-blue-100 dark:bg-blue-800" },
            { label: "Next Visit", value: "Dec 15", color: "bg-purple-100 dark:bg-purple-800" },
            { label: "Satisfaction", value: "100%", color: "bg-yellow-100 dark:bg-yellow-800" }
          ].map((stat, i) => (
            <div
              key={"stat" + i}
              className={`h-20 w-full rounded-lg p-4 flex flex-col justify-center ${stat.color}`}
            >
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</div>
              <div className="text-sm text-gray-600 dark:text-gray-300">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Main Dashboard Content */}
        <div className="flex gap-2 flex-1">
          {/* Recent Activity */}
          <div className="h-full w-1/2 rounded-lg bg-gray-100 dark:bg-neutral-800 p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Recent Activity</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  Weekly inspection completed
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  HVAC maintenance scheduled
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  Garden service update
                </span>
              </div>
            </div>
          </div>

          {/* Upcoming Services */}
          <div className="h-full w-1/2 rounded-lg bg-gray-100 dark:bg-neutral-800 p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Upcoming Services</h3>
            <div className="space-y-3">
              <div className="p-3 bg-white dark:bg-neutral-700 rounded-lg">
                <div className="font-medium text-gray-900 dark:text-white">Deep Cleaning</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Tomorrow, 9:00 AM</div>
              </div>
              <div className="p-3 bg-white dark:bg-neutral-700 rounded-lg">
                <div className="font-medium text-gray-900 dark:text-white">Pool Maintenance</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Dec 18, 2:00 PM</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 